import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePanelStore } from '../store/panel-store.js';
import { SourceEngine, TEST_TONE_DEVICE_ID } from '../lib/source-engine.js';
import { Radio, Wifi, WifiOff } from 'lucide-react';

/**
 * „Quelle"-Panel im Browser: Audioquelle (Mikrofon/Line/Test-Ton) wählen und
 * in die Intercom einspeisen. Andere Panels hören sie über „Listen Port".
 */
export function SourceView(): JSX.Element {
  const { panelId } = useParams<{ panelId: string }>();
  const config = usePanelStore((s) => s.config);
  const configLoaded = usePanelStore((s) => s.configLoaded);
  const loadConfig = usePanelStore((s) => s.loadConfig);

  const engineRef = useRef<SourceEngine | null>(null);
  if (!engineRef.current) engineRef.current = new SourceEngine();
  const engine = engineRef.current;

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>(TEST_TONE_DEVICE_ID);
  const [gain, setGain] = useState(1);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(0);

  const panel = useMemo(
    () => (config && panelId ? config.panels.find((p) => p.id === panelId) : null) ?? null,
    [config, panelId],
  );
  // Name aus der Live-Config, falls vorhanden; sonst Fallback. Zum SENDEN
  // braucht die Seite die Live-Config nicht (die panelId aus der URL reicht) –
  // eine frisch angelegte Quelle ist erst nach „Push" in der Live-Config.
  const panelName = panel?.name ?? 'Quelle';
  // In Live-Config (noch) nicht vorhanden → Hinweis, dass Push fehlt.
  const notLiveYet = configLoaded && !!panelId && !panel;

  useEffect(() => {
    loadConfig().catch((err: Error) => console.warn(err.message));
  }, [loadConfig]);

  // Engine-Status spiegeln + Pegel-Loop.
  useEffect(() => {
    engine.onChange = () => setConnected(engine.connected);
    const t = window.setInterval(() => setLevel(engine.level), 100);
    return () => {
      engine.onChange = () => {};
      window.clearInterval(t);
      void engine.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshDevices(): Promise<void> {
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      probe.getTracks().forEach((t) => t.stop());
    } catch {
      /* Berechtigung verweigert → Labels bleiben leer */
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    setDevices(all.filter((d) => d.kind === 'audioinput'));
  }

  useEffect(() => {
    void refreshDevices();
  }, []);

  async function onConnect(): Promise<void> {
    if (!panelId) return;
    setBusy(true);
    setError(null);
    try {
      await engine.connect(panelId, panelName, deviceId, gain);
      setConnected(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect(): Promise<void> {
    setBusy(true);
    try {
      await engine.disconnect();
    } finally {
      setConnected(false);
      setBusy(false);
    }
  }

  async function onDeviceChange(id: string): Promise<void> {
    setDeviceId(id);
    setError(null);
    try {
      await engine.setDevice(id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function onGainChange(g: number): void {
    setGain(g);
    engine.setGain(g);
  }

  if (!panelId) return <Fallback message="Keine Quelle-ID in der URL." />;
  if (!configLoaded) return <Fallback message="Verbinde…" />;

  return (
    <div className="min-h-full flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface">
        <div className="flex items-center gap-2">
          <Radio size={18} className="text-accent" />
          <h1 className="text-sm font-semibold text-text">{panelName}</h1>
          <span className="text-xxs uppercase tracking-wider text-text-mute">Quelle</span>
        </div>
        <div className="flex items-center gap-2 text-xxs uppercase tracking-wider">
          {connected ? (
            <>
              <Wifi size={14} className="text-ok" />
              <span className="text-text-mute">sendet</span>
            </>
          ) : (
            <>
              <WifiOff size={14} className="text-err" />
              <span className="text-text-mute">aus</span>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 max-w-md w-full mx-auto space-y-5">
        <p className="text-sm text-text-dim">
          Wähle die Audioquelle und sende sie in die Intercom. Andere Panels hören diese Quelle
          über einen „Listen Port"-Button (im Admin verknüpfen).
        </p>

        {notLiveYet ? (
          <div className="rounded-btn border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            Diese Quelle ist noch im Draft (nicht gepusht). <strong>Senden funktioniert trotzdem.</strong>{' '}
            Sobald gesendet wird, erscheint sie im Admin als Port – dort einen „Listen Port"-Button
            anlegen und <strong>Push</strong> drücken, damit andere sie hören.
          </div>
        ) : null}

        {/* Quelle */}
        <div className="card p-4 space-y-3">
          <div className="label-sm text-xxs uppercase tracking-wider text-text-mute">
            Audioquelle
          </div>
          <select
            className="w-full px-2 py-2 rounded bg-surface text-text text-sm border border-border"
            value={deviceId}
            onChange={(e) => void onDeviceChange(e.target.value)}
          >
            <option value={TEST_TONE_DEVICE_ID}>🔊 Test-Ton (1 kHz Sinus)</option>
            <option value="">— System-Standard —</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || d.deviceId.slice(0, 8)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void refreshDevices()}
            className="text-xxs uppercase tracking-wider text-text-mute hover:text-accent"
          >
            Geräte neu laden
          </button>
        </div>

        {/* Gain + Pegel */}
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="label-sm text-xxs uppercase tracking-wider text-text-mute">
              Pegel (Gain {(gain * 100).toFixed(0)} %)
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={2}
            step={0.01}
            value={gain}
            onChange={(e) => onGainChange(parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="h-2 w-full rounded-full bg-black/40 overflow-hidden">
            <div
              className="h-full bg-accent transition-[width] duration-75"
              style={{ width: `${Math.min(100, level * 140)}%` }}
            />
          </div>
        </div>

        {/* Senden */}
        {connected ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onDisconnect()}
            className="w-full py-3 rounded-btn bg-err text-white font-semibold text-sm disabled:opacity-50"
          >
            Senden stoppen
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onConnect()}
            className="w-full py-3 rounded-btn bg-accent text-black font-semibold text-sm disabled:opacity-50"
          >
            {busy ? 'Verbinde…' : 'In Intercom senden'}
          </button>
        )}

        {error ? (
          <div className="text-xs text-err break-words">Fehler: {error}</div>
        ) : null}
      </div>
    </div>
  );
}

function Fallback({ message }: { message: string }): JSX.Element {
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-text-dim text-sm">{message}</p>
    </div>
  );
}
